import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const isIOS = () =>
  /iP(hone|ad|od)/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
export const isAndroid = () => /Android/.test(navigator.userAgent);
export const isTouch = () => "ontouchstart" in window || navigator.maxTouchPoints > 0;
export const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

// Registra el resultado de un permiso para diagnóstico (fire-and-forget).
export function logPermissionEvent(permission, status, extra = {}) {
  try {
    axios.post(
      `${API}/jared/permission-event`,
      { permission, status, user_agent: navigator.userAgent, standalone: isStandalone(), ...extra },
      { withCredentials: true }
    ).catch(() => {});
  } catch { /* noop */ }
}

// Estado actual sin disparar el cuadro nativo: 'granted' | 'denied' | 'prompt' | 'unknown'
export async function queryPermission(name) {
  try {
    if (!navigator.permissions?.query) return "unknown";
    const st = await navigator.permissions.query({ name });
    return st.state;
  } catch { return "unknown"; }
}

async function requestMedia(constraints, permName) {
  if (!navigator.mediaDevices?.getUserMedia) {
    logPermissionEvent(permName, "unsupported");
    return "unsupported";
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    stream.getTracks().forEach((t) => t.stop());
    logPermissionEvent(permName, "granted");
    return "granted";
  } catch (e) {
    const name = e?.name || "";
    const denied = name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError";
    logPermissionEvent(permName, denied ? "denied" : "error", { error: name });
    return denied ? "denied" : "error";
  }
}

export const requestMicrophone = () => requestMedia({ audio: true }, "microphone");
export const requestCamera = () => requestMedia({ video: true }, "camera");

export async function requestNotifications() {
  try {
    if (!("Notification" in window)) { logPermissionEvent("notifications", "unsupported"); return "unsupported"; }
    const res = await Notification.requestPermission();
    logPermissionEvent("notifications", res);
    return res; // 'granted' | 'denied' | 'default'
  } catch { logPermissionEvent("notifications", "error"); return "error"; }
}

export const isOnline = () => navigator.onLine !== false;
export const speakerAvailable = () => "speechSynthesis" in window;
export const speechRecognitionAvailable = () =>
  !!(window.SpeechRecognition || window.webkitSpeechRecognition);
export const notificationsSupported = () =>
  "Notification" in window && (!isIOS() || isStandalone());

// ---- Screen Wake Lock (mantener pantalla encendida) ----
let _wakeLock = null;
export async function acquireWakeLock() {
  try {
    if (!("wakeLock" in navigator)) return false;
    if (_wakeLock) return true;
    _wakeLock = await navigator.wakeLock.request("screen");
    _wakeLock.addEventListener?.("release", () => { _wakeLock = null; });
    return true;
  } catch { _wakeLock = null; return false; }
}
export async function releaseWakeLock() {
  try { await _wakeLock?.release?.(); } catch { /* noop */ }
  _wakeLock = null;
}

// Diagnóstico de sesión: estados de mic/cámara/notificaciones/internet/altavoz/voz.
export async function runSessionDiagnostics() {
  const [mic, cam] = await Promise.all([queryPermission("microphone"), queryPermission("camera")]);
  return {
    microphone: mic,
    camera: cam,
    notifications: notificationsSupported() ? (Notification.permission) : "unsupported",
    internet: isOnline(),
    speaker: speakerAvailable(),
    speechRecognition: speechRecognitionAvailable(),
  };
}

// Instrucciones para reactivar un permiso denegado (no se puede abrir Ajustes desde una PWA).
export function settingsInstructions(permissionLabel = "el micrófono") {
  if (isIOS()) {
    return [
      `Abre Ajustes del iPhone.`,
      `Baja hasta encontrar esta app (o Safari) y tócala.`,
      `Activa ${permissionLabel}.`,
      `Regresa a la app y toca “Reintentar”.`,
    ];
  }
  if (isAndroid()) {
    return [
      `Mantén presionado el ícono de la app y abre “Información de la app”.`,
      `Entra a “Permisos”.`,
      `Activa ${permissionLabel}.`,
      `Regresa a la app y toca “Reintentar”.`,
    ];
  }
  return [
    `Haz clic en el ícono de candado 🔒 junto a la dirección del sitio.`,
    `Busca ${permissionLabel} y cámbialo a “Permitir”.`,
    `Recarga y toca “Reintentar”.`,
  ];
}
